import {
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    TextField,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "../../../../explorer/hooks/useTranslation";
import { WarrantyItemForMissing, WarrantyReason } from "../../../types";
import {
    DialogReasonContainer,
    StyledInputBox,
    StyledInputBoxRadio,
} from "./MissingPartsWrongMaterial.style";
import { AppIconsArrow } from "../../AppIcons";

type TMissingPartsWrongMaterialReason = {
    item: WarrantyItemForMissing;
    errorList: { key: string; indexes: string[] }[];
};

const MissingPartsWrongMaterialReason: FC<TMissingPartsWrongMaterialReason> = (
    props
) => {
    const { item, errorList } = props;

    const [reason, setReason] = useState<WarrantyReason | undefined>(item.case);
    const [expected, setExpanded] = useState<number | null>(
        item.expectedQuantity ?? 1
    );
    const [received, setReceived] = useState<number | null>(
        item.quantityReceived ?? null
    );
    const [required, setRequired] = useState(0);
    const [isScrapped, setIsScrapped] = useState<boolean>(
        item.scrappedMaterialOnSite ?? true
    );

    const { t } = useTranslation();

    const handleChange = (event: SelectChangeEvent) => {
        setReason(event.target.value as WarrantyReason);
    };

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsScrapped(
            event.target.value === "yes" &&
                reason === WarrantyReason.WrongMaterial
        );
    };

    const handleInputChange =
        (setter: React.Dispatch<React.SetStateAction<number | null>>) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value = event.target.value;

            setter(value === "" ? null : Math.abs(Number(value)));
        };

    useEffect(() => {
        if (received !== null && expected !== null) {
            setRequired(expected - received);
        }

        item.expectedQuantity = expected;
        item.quantityReceived = received;
    }, [expected, received]);

    useEffect(() => {
        if (required) item.required = required;
    }, [required]);

    useEffect(() => {
        item.case = reason;
    }, [reason]);

    useEffect(() => {
        item.scrappedMaterialOnSite = isScrapped;
    }, [isScrapped]);

    return (
        <DialogReasonContainer>
            <StyledInputBox>
                <InputLabel id="select-reason">
                    {t("warranty.reason")}
                </InputLabel>
                <FormControl
                    sx={{
                        display: "flex",
                        width: 200,
                    }}
                    required
                    variant="standard"
                >
                    <Select
                        id="reason"
                        value={reason ?? ""}
                        onChange={handleChange}
                        error={errorList.some(
                            (x) =>
                                x.key.includes("case") &&
                                x.indexes.includes(item.rowId)
                        )}
                        IconComponent={AppIconsArrow}
                    >
                        <MenuItem value={WarrantyReason.MissingPart}>
                            {t(
                                `warranty.${WarrantyReason.MissingPart.toString()}`
                            )}
                        </MenuItem>
                        <MenuItem value={WarrantyReason.WrongMaterial}>
                            {t(
                                `warranty.${WarrantyReason.WrongMaterial.toString()}`
                            )}
                        </MenuItem>
                    </Select>
                </FormControl>
            </StyledInputBox>

            <StyledInputBox sx={{ ml: 2 }}>
                <InputLabel id="ExpectedQty">
                    {t("warranty.ExpectedQty")}
                </InputLabel>
                <TextField
                    id="ExpectedQty_Input"
                    type="number"
                    variant="standard"
                    slotProps={{
                        htmlInput: {
                            min: 1,
                            style: {
                                textAlign: "right",
                            },
                        },
                    }}
                    onChange={handleInputChange(setExpanded)}
                    sx={{ width: 90 }}
                    value={expected ?? ""}
                    error={errorList.some(
                        (x) =>
                            x.key.includes("expected") &&
                            x.indexes.includes(item.rowId)
                    )}
                />
            </StyledInputBox>

            <StyledInputBox sx={{ ml: 2 }}>
                <InputLabel id="QtyReceived">
                    {t("warranty.QtyReceived")}
                </InputLabel>
                <TextField
                    id="QtyReceived_Input"
                    type="number"
                    variant="standard"
                    slotProps={{
                        htmlInput: {
                            min: 0,
                            max: expected,
                            style: {
                                textAlign: "right",
                            },
                        },
                    }}
                    onChange={handleInputChange(setReceived)}
                    sx={{ width: 90 }}
                    value={received ?? ""}
                    error={errorList.some(
                        (x) =>
                            x.key.includes("received") &&
                            x.indexes.includes(item.rowId)
                    )}
                />
            </StyledInputBox>

            <StyledInputBox sx={{ ml: 2 }}>
                <InputLabel id="QtyRequired">
                    {t("warranty.QtyRequired")}
                </InputLabel>
                <TextField
                    disabled
                    value={required}
                    sx={{ width: 90 }}
                    variant="standard"
                />
            </StyledInputBox>

            {reason === WarrantyReason.WrongMaterial && (
                <StyledInputBoxRadio sx={{ ml: 2 }}>
                    <FormControl>
                        <FormLabel id="scrappedMaterialSite_label">
                            {t("warranty.scrappedMaterialSite")}
                        </FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="scrappedMaterialSite_label"
                            name="scrappedMaterialSite_group"
                            onChange={handleRadioChange}
                            defaultValue={isScrapped ? "yes" : "no"}
                        >
                            <FormControlLabel
                                value={"yes"}
                                control={<Radio />}
                                label={t("common.yes")}
                            />
                            <FormControlLabel
                                value={"no"}
                                control={<Radio />}
                                label={t("common.no")}
                            />
                        </RadioGroup>
                    </FormControl>
                </StyledInputBoxRadio>
            )}
        </DialogReasonContainer>
    );
};

export default MissingPartsWrongMaterialReason;
